import httpClient from './httpClient';
import type { PaginatedResult } from '@/types';
import type {
  InstalledEquipmentItem,
  InstalledEquipmentDetail,
  InstalledEquipmentFilters,
  CreateInstalledEquipmentRequest,
  UpdateInstalledEquipmentRequest,
  ServiceRequestItem,
  ServiceRequestDetail,
  ServiceRequestFilters,
  CreateServiceRequestRequest,
  UpdateServiceRequestRequest,
  AddServicePartRequest,
  SlaSummary,
  ServiceEngineer,
} from '@/types/service';

export const serviceApi = {
  // ── Installed Equipment ─────────────────────────────────

  async getInstalledEquipment(
    mdFilters: InstalledEquipmentFilters
  ): Promise<PaginatedResult<InstalledEquipmentItem>> {
    const mdParams: Record<string, string | number | boolean> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.customerId) mdParams.customerId = mdFilters.customerId;
    if (mdFilters.status) mdParams.status = mdFilters.status;
    if (mdFilters.warrantyExpiring !== undefined) mdParams.warrantyExpiring = mdFilters.warrantyExpiring;

    const mdResponse = await httpClient.get<PaginatedResult<InstalledEquipmentItem>>(
      '/installed-equipment',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getInstalledEquipmentById(mdId: string): Promise<InstalledEquipmentDetail> {
    const mdResponse = await httpClient.get<InstalledEquipmentDetail>(
      `/installed-equipment/${mdId}`
    );
    return mdResponse.data;
  },

  async searchInstalledEquipment(mdQuery: string): Promise<InstalledEquipmentItem[]> {
    const mdResponse = await httpClient.get<InstalledEquipmentItem[]>(
      '/installed-equipment/search',
      { params: { q: mdQuery } }
    );
    return mdResponse.data;
  },

  async getWarrantyExpiring(): Promise<InstalledEquipmentItem[]> {
    const mdResponse = await httpClient.get<InstalledEquipmentItem[]>(
      '/installed-equipment/warranty-expiring'
    );
    return mdResponse.data;
  },

  // Сервер повертає лише id створеного запису
  async createInstalledEquipment(
    mdRequest: CreateInstalledEquipmentRequest
  ): Promise<string> {
    const mdResponse = await httpClient.post<string>(
      '/installed-equipment',
      mdRequest
    );
    return mdResponse.data;
  },

  async updateInstalledEquipment(
    mdRequest: UpdateInstalledEquipmentRequest
  ): Promise<void> {
    await httpClient.put(`/installed-equipment/${mdRequest.id}`, mdRequest);
  },

  // Soft delete; сервер відмовляє (400), поки є відкриті заявки на це обладнання
  async deleteInstalledEquipment(mdId: string): Promise<void> {
    await httpClient.delete(`/installed-equipment/${mdId}`);
  },

  // ── Service Requests ────────────────────────────────────

  async getServiceRequests(
    mdFilters: ServiceRequestFilters
  ): Promise<PaginatedResult<ServiceRequestItem>> {
    const mdParams: Record<string, string | number | boolean> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.customerId) mdParams.customerId = mdFilters.customerId;
    if (mdFilters.status) mdParams.status = mdFilters.status;
    if (mdFilters.priority) mdParams.priority = mdFilters.priority;
    if (mdFilters.assignedEngineerId) mdParams.assignedEngineerId = mdFilters.assignedEngineerId;
    if (mdFilters.slaBreach !== undefined) mdParams.slaBreach = mdFilters.slaBreach;

    const mdResponse = await httpClient.get<PaginatedResult<ServiceRequestItem>>(
      '/service-requests',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getServiceRequestById(mdId: string): Promise<ServiceRequestDetail> {
    const mdResponse = await httpClient.get<ServiceRequestDetail>(
      `/service-requests/${mdId}`
    );
    return mdResponse.data;
  },

  // Сервер повертає лише id створеної заявки
  async createServiceRequest(
    mdRequest: CreateServiceRequestRequest
  ): Promise<string> {
    const mdResponse = await httpClient.post<string>(
      '/service-requests',
      mdRequest
    );
    return mdResponse.data;
  },

  async updateServiceRequest(
    mdRequest: UpdateServiceRequestRequest
  ): Promise<void> {
    await httpClient.put(`/service-requests/${mdRequest.id}`, mdRequest);
  },

  // Тіло команди містить id — сервер звіряє його з маршрутом
  async assignServiceRequest(mdId: string, mdEngineerId: string): Promise<void> {
    await httpClient.post(`/service-requests/${mdId}/assign`, { id: mdId, engineerId: mdEngineerId });
  },

  async startServiceRequest(mdId: string): Promise<void> {
    await httpClient.post(`/service-requests/${mdId}/start`);
  },

  async completeServiceRequest(mdId: string, mdResolution: string): Promise<void> {
    await httpClient.post(`/service-requests/${mdId}/complete`, { id: mdId, resolution: mdResolution });
  },

  async closeServiceRequest(mdId: string): Promise<void> {
    await httpClient.post(`/service-requests/${mdId}/close`);
  },

  // Сервер повертає id запису про запчастину
  async addServicePart(mdRequestId: string, mdPart: AddServicePartRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>(`/service-requests/${mdRequestId}/parts`, {
      ...mdPart,
      serviceRequestId: mdRequestId,
    });
    return mdResponse.data;
  },

  // Лише для заявок InProgress / WaitingForParts; склад не змінюється — запчастини не списуються з партій
  async removeServicePart(mdRequestId: string, mdPartId: string): Promise<void> {
    await httpClient.delete(`/service-requests/${mdRequestId}/parts/${mdPartId}`);
  },

  // Активні користувачі ролі ServiceEngineer (для призначення та фільтра)
  async getServiceEngineers(): Promise<ServiceEngineer[]> {
    const mdResponse = await httpClient.get<ServiceEngineer[]>('/service-requests/engineers');
    return mdResponse.data;
  },

  // ── SLA Dashboard ───────────────────────────────────────

  async getSlaSummary(): Promise<SlaSummary> {
    const mdResponse = await httpClient.get<SlaSummary>(
      '/service-requests/sla-dashboard'
    );
    return mdResponse.data;
  },
};
